import type { IconType } from "react-icons";
import { FiAlertOctagon, FiAlertTriangle, FiCheckCircle, FiClock, FiHelpCircle, FiXOctagon } from "react-icons/fi";

import { useI18n, type Key } from "@/lib/i18n";
import type { OltState, PonTier } from "@/lib/olthealth";
import { cn } from "@/lib/utils";

/** One visual per state: icon + word + colour, never colour alone. */
export const OLT_STATE: Record<OltState, { icon: IconType; text: string; bg: string; label: Key }> = {
  healthy: { icon: FiCheckCircle, text: "text-signal-good", bg: "bg-signal-good/10", label: "olt.state.healthy" },
  degraded: { icon: FiAlertTriangle, text: "text-signal-warn", bg: "bg-signal-warn/10", label: "olt.state.degraded" },
  critical: { icon: FiAlertOctagon, text: "text-signal-crit", bg: "bg-signal-crit/10", label: "olt.state.critical" },
  down: { icon: FiXOctagon, text: "text-white", bg: "bg-signal-crit", label: "olt.state.down" },
  stale: { icon: FiClock, text: "text-amber-200", bg: "bg-amber-400/10", label: "olt.state.stale" },
  unknown: { icon: FiHelpCircle, text: "text-slate-300", bg: "bg-slate-700/40", label: "olt.state.unknown" },
};

/** Rank for sorting a fleet: the thing that needs a human comes first. */
export const OLT_STATE_RANK: Record<OltState, number> = { down: 0, critical: 1, stale: 2, degraded: 3, unknown: 4, healthy: 5 };

export function OltStateBadge({ state, big }: { state: OltState; big?: boolean }) {
  const { t } = useI18n();
  const s = OLT_STATE[state];
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-sm font-semibold uppercase tracking-[0.12em]", s.text, s.bg,
      big ? "px-2.5 py-1 text-[12.5px]" : "px-1.5 py-0.5 text-[10.5px]")}>
      <s.icon className={big ? "size-4" : "size-3"} /> {t(s.label)}
    </span>
  );
}

/** PON / ONT marker: filled square = down, ring = degraded or weak, small dot = fine. */
export function TierMark({ tier, className }: { tier: PonTier | "crit" | "warn"; className?: string }) {
  const down = tier === "down" || tier === "crit";
  const ring = tier === "degraded" || tier === "warn";
  return (
    <span
      aria-hidden
      className={cn(
        "inline-block shrink-0",
        down ? "size-2.5 rounded-[2px] bg-signal-crit" : ring ? "size-2.5 rounded-full border-2" : "size-1.5 rounded-full bg-signal-good/80",
        tier === "degraded" && "border-signal-warn",
        tier === "warn" && "border-signal-warn/70",
        className,
      )}
    />
  );
}

/** Online/total as a thin bar: reads at a glance down a long fleet or PON table. */
export function OnlineBar({ online, total }: { online: number; total: number }) {
  const pct = total ? (online / total) * 100 : 0;
  return (
    <span className="block h-1 w-full overflow-hidden rounded-full bg-rule" aria-hidden>
      <span className={cn("block h-full", online === total ? "bg-signal-good/60" : online === 0 ? "bg-signal-crit" : "bg-signal-warn")}
        style={{ width: `${total ? Math.max(pct, 2) : 0}%` }} />
    </span>
  );
}

/** "8s", "4m 12s", "2h 05m": compact age for poll times. */
export function ageOf(iso: string | null | undefined, nowMs: number): string {
  if (!iso) return "—";
  const s = Math.max(0, Math.floor((nowMs - Date.parse(iso)) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, "0")}s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${String(m % 60).padStart(2, "0")}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

/** Uptime, the way the NOC says it: "23d 17h". */
export function uptimeOf(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return "—";
  const d = Math.floor(seconds / 86400), h = Math.floor((seconds % 86400) / 3600), m = Math.floor((seconds % 3600) / 60);
  return d ? `${d}d ${h}h` : h ? `${h}h ${m}m` : `${m}m`;
}
